import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Media } from "reactstrap";
import { COLOR_DANGER } from "../../constants/theme";

interface Props {
  id?: string;
  label?: string;
  className?: string;
  image?: string;
  fallbackImage?: string;
  active?: boolean;
  icon?: ReactNode;
  style?: CSSProperties;
  height?: string;
  width?: string;
  backgroundColor?: string;
  color?: string;
  labelFontSize?: number;
  onClick?: () => void;
}

const normalizeImage = (image?: string) =>
  image && !["null", "undefined", ""].includes(String(image).trim())
    ? String(image).trim()
    : undefined;

export default function Avatar(props: Props) {
  const {
    id,
    label,
    className,
    image,
    fallbackImage,
    active = true,
    icon,
    backgroundColor = "#f8f9fa",
    color = "#000",
    height = "40px",
    width = "40px",
    style,
    labelFontSize,
    onClick,
  } = props;

  const [imgError, setImgError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);

  const normalized = useMemo(() => {
    const normalize = (v?: string) => {
      if (!v) return v;
      const trimmed = String(v).trim();
      if (/^\d+$/.test(trimmed)) return `${trimmed}px`;
      return trimmed;
    };
    const primary = normalizeImage(image);
    const fallback = normalizeImage(fallbackImage);
    const activeImage = useFallback ? fallback || primary : primary || fallback;
    return {
      width: normalize(width),
      height: normalize(height),
      primary,
      fallback,
      image: activeImage,
    };
  }, [width, height, image, fallbackImage, useFallback]);

  useEffect(() => {
    setImgError(false);
    setUseFallback(false);
  }, [normalized.primary, normalized.fallback]);

  const handleImageError = () => {
    if (!useFallback && normalized.fallback && normalized.primary) {
      setUseFallback(true);
      setImgError(false);
      return;
    }
    setImgError(true);
  };

  return (
    <div
      id={id}
      style={{
        width: normalized.width,
        height: normalized.height,
        minHeight: normalized.height,
        minWidth: normalized.width,
        boxSizing: "border-box",
        overflow: "hidden",
        backgroundColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
      className={`${className} d-flex rounded-circle position-relative`}
      onClick={onClick}
    >
      {!active && (
        <FontAwesomeIcon
          icon={["fas", "ban"]}
          size="1x"
          className="position-absolute"
          color={COLOR_DANGER}
        />
      )}
      {normalized.image && !imgError ? (
        <Media
          style={{
            width: normalized.width,
            height: normalized.height,
            display: "block",
            flexShrink: 0,
            objectFit: "cover",
            borderRadius: "50%",
          }}
          object
          src={normalized.image}
          alt="Generic placeholder image"
          onError={handleImageError}
        />
      ) : label ? (
        <span className="m-auto text-primary " style={{ color, fontSize: labelFontSize }}>
          {label?.toUpperCase()}
        </span>
      ) : (
        <span className="m-auto" style={{ color }}>
          {icon}
        </span>
      )}
    </div>
  );
}
